import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FolderKanban, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { API, AUDIT_LABELS, STATUS_META, errorText, sourceLabel } from './constants';
import { ApprovalProgress, BlockedBox, WarningsBox } from './ProposalGovernanceParts';

const RISK_NAMES = { critical: 'Crítico', high: 'Alto', medium: 'Medio', low: 'Bajo' };

const Field = ({ label, value }) => (value === null || value === undefined || value === '' ? null : (
  <div><p className="text-xs uppercase tracking-wide text-slate-400">{label}</p><p className="text-sm text-slate-800 dark:text-slate-200">{String(value)}</p></div>
));

const DecisionBox = ({ companyId, proposal }) => {
  const qc = useQueryClient();
  const [comment, setComment] = useState('');
  const decide = useMutation({
    mutationFn: (decision) => axios.post(`${API}/companies/${companyId}/proposals/${proposal.id}/decision`, { decision, comment }),
    onSuccess: (r, d) => {
      const st = r.data.proposal.status;
      toast.success(d === 'reject' ? 'Decisión rechazada' : st === 'approved' ? `Decisión aprobada${r.data.project_id ? ' · proyecto creado en Proyectos' : ''}` : 'Tu aprobación quedó registrada; faltan otros aprobadores');
      qc.invalidateQueries({ queryKey: ['projects', companyId] }); qc.invalidateQueries({ queryKey: ['portfolio', companyId] });
      qc.invalidateQueries({ queryKey: ['v2-proposals', companyId] });
      qc.invalidateQueries({ queryKey: ['v2-proposal', proposal.id] });
    },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/30">
      <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Justificación de la decisión (obligatoria)" data-testid="proposal-decision-comment" />
      <div className="flex gap-2">
        <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={comment.trim().length < 3 || decide.isPending} onClick={() => decide.mutate('approve')} data-testid="proposal-approve-btn"><CheckCircle2 className="mr-2 h-4 w-4" />Aprobar</Button>
        <Button variant="destructive" disabled={comment.trim().length < 3 || decide.isPending} onClick={() => decide.mutate('reject')} data-testid="proposal-reject-btn"><XCircle className="mr-2 h-4 w-4" />Rechazar</Button>
      </div>
    </div>
  );
};

const ProjectBox = ({ companyId, proposal, project }) => {
  const qc = useQueryClient();
  const create = useMutation({
    mutationFn: () => axios.post(`${API}/companies/${companyId}/proposals/${proposal.id}/project`),
    onSuccess: () => {
      toast.success('Proyecto creado en Proyectos');
      qc.invalidateQueries({ queryKey: ['projects', companyId] }); qc.invalidateQueries({ queryKey: ['portfolio', companyId] });
      qc.invalidateQueries({ queryKey: ['v2-proposal', proposal.id] });
    },
    onError: (e) => toast.error(errorText(e)),
  });
  if (project) return (
    <p className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm dark:border-emerald-900 dark:bg-emerald-950/40" data-testid="proposal-project-linked">
      <FolderKanban className="h-4 w-4 shrink-0 text-emerald-600" /><span>En ejecución: proyecto <b>{project.title}</b> (míralo en Proyectos)</span></p>);
  if (proposal.status !== 'auto_cleared') return null;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 p-3 dark:border-sky-900 dark:bg-sky-950/30">
      <p className="mr-auto text-sm text-slate-600 dark:text-slate-300">Esta decisión no requiere aprobación. ¿Pasarla a ejecución?</p>
      <Button onClick={() => create.mutate()} disabled={create.isPending} data-testid="proposal-create-project-btn">
        <FolderKanban className="mr-2 h-4 w-4" />{create.isPending ? 'Creando…' : 'Crear proyecto'}</Button>
    </div>
  );
};

const ProposalDetailDialog = ({ companyId, proposalId, user, onClose }) => {
  const { data } = useQuery({
    queryKey: ['v2-proposal', proposalId],
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/proposals/${proposalId}`)).data,
  });
  const p = data?.proposal;
  const perms = data?.my_permissions;
  const canDecide = p?.status === 'pending' && perms?.can_approve;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="proposal-detail-dialog">
        <DialogHeader><DialogTitle>{p?.title || 'Cargando…'}</DialogTitle></DialogHeader>
        {p && (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2">
              <Badge className={STATUS_META[p.status]?.cls} data-testid="proposal-detail-status">{STATUS_META[p.status]?.label}</Badge>
              {p.required_role && <Badge variant="outline">Requiere: {p.required_role === 'super_admin' ? 'Super Admin' : 'Admin'}</Badge>}
              <Badge variant="outline">Origen: {sourceLabel(p)}</Badge>
            </div>
            {p.description && <p className="text-sm text-slate-600 dark:text-slate-300">{p.description}</p>}
            <div className="grid grid-cols-3 gap-3">
              <Field label="Monto" value={p.amount} /><Field label="Impacto %" value={p.impact_pct} /><Field label="Riesgo" value={RISK_NAMES[p.risk_level] || p.risk_level} />
              <Field label="Categoría" value={p.category} /><Field label="Módulo" value={p.module_type} /><Field label="Expira" value={p.expires_at && new Date(p.expires_at).toLocaleString()} />
            </div>
            {p.routing && (
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm dark:border-sky-900 dark:bg-sky-950/30" data-testid="proposal-routing">
                <p className="mb-1 text-xs font-medium text-slate-500">Routing IAD</p>
                <p><span className="font-mono font-semibold text-amber-700 dark:text-amber-400">{p.routing.iad_code}</span> · {p.routing.iad_name} → {p.routing.gov} → {p.routing.agent_name}</p>
              </div>
            )}
            {p.matched_rules?.length > 0 && (
              <div><p className="mb-1 text-xs font-medium text-slate-500">Reglas activadas</p>
                {p.matched_rules.map((m) => <p key={m.rule_id} className="text-sm" data-testid={`proposal-matched-rule-${m.rule_id}`}>• {m.name} (v{m.version}, {m.severity})</p>)}
              </div>
            )}
            {p.status === 'blocked' && <BlockedBox companyId={companyId} p={p} perms={perms} />}
            <WarningsBox warnings={p.warnings} />
            {p.status === 'pending' && <ApprovalProgress progress={data.approval_progress} approvals={p.approvals} />}
            {p.exceptions?.length > 0 && p.exceptions.map((e) => <p key={e.rule_id} className="text-sm" data-testid={`proposal-exception-granted-${e.rule_id}`}><b>Excepción</b> de {e.granted_by}: {e.justification}</p>)}
            {p.decision_comment && <p className="text-sm"><span className="font-medium">Decisión de {p.decided_by}:</span> {p.decision_comment}</p>}
            {canDecide && <DecisionBox companyId={companyId} proposal={p} />}
            <ProjectBox companyId={companyId} proposal={p} project={data.project} />
            {p.status === 'pending' && !canDecide && <p className="text-sm text-slate-500" data-testid="proposal-no-permission">{perms?.already_approved ? 'Ya registraste tu aprobación; faltan otros aprobadores.' : 'No te corresponde decidir esta propuesta en este momento.'}</p>}
            <div data-testid="proposal-audit-trail"><p className="mb-2 text-xs font-medium text-slate-500">Auditoría</p>
              <ol className="space-y-1 border-l-2 border-slate-200 pl-4 dark:border-slate-700">
                {(data.audit || []).map((a) => <li key={a.id} className="text-xs text-slate-600 dark:text-slate-400">{new Date(a.at).toLocaleString()} · <b>{AUDIT_LABELS[a.action] || a.action}</b>{a.details?.rule_name ? ` (${a.details.rule_name} v${a.details.version})` : ''} · {a.actor}{a.details?.comment ? ` · “${a.details.comment}”` : ''}</li>)}
              </ol>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProposalDetailDialog;
